<script lang="ts">
  /**
   * Inline-edit для одного значення поля.
   * - Клік на value → input/select з’являється
   * - Enter / blur → save (asyncз callback'ом onSave)
   * - Esc → cancel
   * Якщо `readOnly` — просто рендериться значення з пов'язаною підказкою.
   */
  import Icon from './Icon.svelte';

  let {
    value,
    onSave,
    readOnly = false,
    type = 'text',
    options,
    placeholder,
    mono = false,
  }: {
    value: string | null | undefined;
    onSave: (next: string | null) => Promise<void>;
    readOnly?: boolean;
    type?: 'text' | 'date' | 'datetime' | 'select';
    /** Для type='select'. */
    options?: { value: string; label: string }[];
    placeholder?: string;
    mono?: boolean;
  } = $props();

  let editing = $state(false);
  let draft = $state('');
  let busy = $state(false);
  let err = $state<string | null>(null);

  function start() {
    if (readOnly || busy) return;
    draft = value ?? '';
    editing = true;
    err = null;
  }
  function cancel() {
    editing = false;
    err = null;
  }
  async function commit() {
    if (!editing) return;
    const next = draft.trim();
    const current = (value ?? '').trim();
    if (next === current) {
      editing = false;
      return;
    }
    busy = true;
    err = null;
    try {
      await onSave(next === '' ? null : next);
      editing = false;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && type !== 'select') {
      e.preventDefault();
      void commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }
</script>

{#if editing}
  <div class="flex items-center gap-1 w-full">
    {#if type === 'select' && options}
      <select
        bind:value={draft}
        onkeydown={onKey}
        disabled={busy}
        class="flex-1 rounded border border-[var(--color-pu-3)] bg-white px-2 py-0.5 text-[12px] outline-none"
      >
        <option value=""></option>
        {#each options as opt (opt.value)}
          <option value={opt.value}>{opt.label}</option>
        {/each}
      </select>
    {:else}
      <input
        type={type === 'date' ? 'date' : type === 'datetime' ? 'datetime-local' : 'text'}
        bind:value={draft}
        onkeydown={onKey}
        placeholder={placeholder ?? ''}
        disabled={busy}
        class="flex-1 rounded border border-[var(--color-pu-3)] bg-white px-2 py-0.5 text-[12.5px] outline-none {mono ? 'font-mono text-[11.5px]' : ''}"
      />
    {/if}
    <button
      onclick={commit}
      disabled={busy}
      title="Зберегти"
      class="inline-flex items-center rounded px-1.5 py-0.5 bg-[var(--color-pu)] text-white hover:opacity-90 disabled:opacity-50"
    ><Icon name="check" size={13} /></button>
    <button
      onclick={cancel}
      disabled={busy}
      title="Скасувати"
      class="inline-flex items-center rounded px-1.5 py-0.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
    ><Icon name="close" size={13} /></button>
  </div>
  {#if err}<div class="text-[10px] text-red-600 mt-0.5">{err}</div>{/if}
{:else}
  <button
    type="button"
    onclick={start}
    disabled={readOnly}
    class="block w-full text-left text-[12.5px] truncate rounded px-1 py-0.5 -mx-1
      {value ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted-2)] italic'}
      {mono ? 'font-mono text-[11.5px]' : ''}
      {readOnly ? 'cursor-default' : 'cursor-pointer hover:bg-[var(--color-bg-3)]'}"
  >
    {value || '—'}
  </button>
{/if}
